<template>
  <div>
    <div
      v-for="(process, idx) in assessPlan.processes"
      :key="idx"
      class="riskSheet q-mb-lg"
    >
      <div class="processBigTitle">
        [{{ process.assessDeptName }}] {{ process.processName }}
      </div>
      <!-- 상단 정보 : 팀명/공정명/원료(좌) - 제목 - 근로자수/생산품(우) -->
      <table class="headerTable">
        <colgroup>
          <col style="width: 90px" />
          <col style="width: 200px" />
          <col />
          <col style="width: 90px" />
          <col style="width: 200px" />
        </colgroup>
        <tbody>
          <tr>
            <th class="hLabel">팀명(반)</th>
            <td class="hValue">{{ process.assessDeptName }}</td>
            <td class="mainTitle" rowspan="3">안전보건상 위험정보</td>
            <th class="hLabel">근로자수</th>
            <td class="hValue">{{ process.workerCnt }}명</td>
          </tr>
          <tr>
            <th class="hLabel">공정명</th>
            <td class="hValue">{{ process.processName }}</td>
            <th class="hLabel">생산품(기종)</th>
            <td class="hValue">{{ process.productName }}</td>
          </tr>
          <tr>
            <th class="hLabel">원(재)료</th>
            <td class="hValue">{{ process.mainMaterial }}</td>
            <th class="hLabel"></th>
            <td class="hValue"></td>
          </tr>
        </tbody>
      </table>
      <!-- 단위작업 / 설비 / 화학물질 / 기타 안전보건상 정보 -->
      <table class="riskTable">
        <colgroup>
          <col style="width: 150px" />
          <col style="width: 150px" />
          <col style="width: 60px" />
          <col style="width: 120px" />
          <col style="width: 80px" />
          <col style="width: 80px" />
          <col style="width: 450px" />
        </colgroup>
        <thead>
          <tr>
            <th rowspan="2" class="tHead">단위작업(순서)</th>
            <th colspan="2" class="tHead">기계,기구 및 설비</th>
            <th colspan="3" class="tHead">유해화학물질</th>
            <th rowspan="2" class="tHead">기타 안전보건상 정보</th>
          </tr>
          <tr>
            <th class="tSubHead">기계,기구 및 설비 명</th>
            <th class="tSubHead">수량</th>
            <th class="tSubHead">화학물질명</th>
            <th class="tSubHead">일 취급량</th>
            <th class="tSubHead">취급시간</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(row, rIdx) in process._rows">
            <tr :key="idx + '_' + rIdx">
              <td
                v-if="row.isFirstWorkRow"
                :rowspan="row.workRowSpan"
                class="cLeft"
              >
                {{ row.unitWorkName }}
              </td>
              <td class="cLeft">{{ row.equipName }}</td>
              <td class="cCenter">{{ row.equipQty }}</td>
              <td class="cLeft">{{ row.chemName }}</td>
              <td class="cCenter">{{ row.chemQty }}</td>
              <td class="cCenter">{{ row.chemTime }}</td>
              <td
                v-if="rIdx === 0"
                :rowspan="process._rows.length"
                class="etcInfoCell"
              >
                <!-- 최근 3년간 재해발생사례 -->
                <div class="etcRow">
                  ◈ 최근 3년간 재해발생사례 :
                  {{ getAccidentNames(process.accidentHistoryDesc) }}
                </div>
                <!-- 아차사고사례 -->
                <div class="etcRow">
                  ◈ 아차사고사례 : {{ getAccidentNames(process.nearMissDesc) }}
                </div>
                <!-- 근로자 구성 및 경력특성 -->
                <div class="etcRow">◈ 근로자 구성 및 경력특성</div>
                <div class="chipGrid">
                  <span
                    v-for="citem in workerTypeItems"
                    :key="citem.code"
                    class="chip"
                    :class="{
                      chipOn: isSelected(process.workerTypeCd, citem.code),
                    }"
                  >
                    {{ citem.codeName }}
                  </span>
                </div>
                <!-- 교대작업 유무 -->
                <div class="etcRow">
                  ◈ 교대작업유무 :
                  <span
                    class="chip"
                    :class="{ chipOn: process.shiftFlag === 'Y' }"
                    >유</span
                  >
                  <span
                    class="chip"
                    :class="{ chipOn: process.shiftFlag !== 'Y' }"
                    >무</span
                  >
                </div>
                <!-- 중량물 운반수단 -->
                <div class="etcRow">
                  ◈ 중량물 운반수단 :
                  <span
                    v-for="citem in transportTypeItems"
                    :key="citem.code"
                    class="chip"
                    :class="{
                      chipOn: isSelected(
                        process.heavyTransportTypeCd,
                        citem.code
                      ),
                    }"
                  >
                    {{ citem.codeName }}
                  </span>
                </div>
                <div class="etcRow etcSub">
                  ▶ 장비명 : {{ process.heavyTransportEquip }}
                </div>
                <!-- 중량물 인력취급유무 -->
                <div class="etcRow">
                  ◈ 중량물 인력취급유무 :
                  <span
                    class="chip"
                    :class="{ chipOn: process.heavyManualFlag === 'Y' }"
                    >유</span
                  >
                  <span
                    class="chip"
                    :class="{ chipOn: process.heavyManualFlag !== 'Y' }"
                    >무</span
                  >
                </div>
                <div class="etcRow etcSub">
                  ▶ 취급형태 :
                  <span
                    v-for="citem in handlingTypeItems"
                    :key="citem.code"
                    class="chip"
                    :class="{
                      chipOn: isSelected(process.heavyManualTypeCd, citem.code),
                    }"
                  >
                    {{ citem.codeName }}
                  </span>
                </div>
                <!-- 작업환경측정 실시 -->
                <div class="etcRow">
                  ◈ 작업환경측정 실시 :
                  <span
                    class="chip"
                    :class="{ chipOn: process.workEnvMeasureFlag === 'Y' }"
                    >유</span
                  >
                  <span
                    class="chip"
                    :class="{ chipOn: process.workEnvMeasureFlag !== 'Y' }"
                    >무</span
                  >
                </div>
                <div class="etcRow etcSub">
                  ▶ 노출초과인자/수치 : {{ process.exposureOverValue }}
                </div>
                <!-- 특별안전보건교육 해당 -->
                <div class="etcRow">◈ 특별안전보건교육 해당</div>
                <div class="chipGrid">
                  <span
                    v-for="citem in specialEduItems"
                    :key="citem.code"
                    class="chip"
                    :class="{
                      chipOn: isSelected(
                        process.specialEduTargetCd,
                        citem.code
                      ),
                    }"
                  >
                    {{ citem.codeName }}
                  </span>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import selectConfig from "@/js/selectConfig";
export default {
  name: "risk-process-detail-PL",
  props: {
    param: {
      type: Object,
      default: () => ({
        ramRiskAssessmentPlanId: "",
        ramStepCd: "",
        vendorFlag: false,
      }),
    },
  },
  data() {
    return {
      assessPlan: {
        ramRiskAssessmentPlanId: "",
        processes: [],
      },
      detailUrl: "",
      accidentDetailUrl: "",
      accidentNameMap: {},
      workerTypeItems: [],
      transportTypeItems: [],
      handlingTypeItems: [],
      specialEduItems: [],
    };
  },
  beforeCreate() {},
  created() {},
  beforeMount() {
    Object.assign(this.$data, this.$options.data());
  },
  mounted() {
    this.init();
  },
  beforeDestroy() {},
  methods: {
    init() {
      // url setting
      this.detailUrl = selectConfig.ram.assessPlan.get.url;
      this.accidentDetailUrl = selectConfig.sop.iim.accident.process.get.url;
      // code setting
      this.$comm.getComboItems("MDM_PROCESS_WORKER_TYPE_CD").then((_result) => {
        this.workerTypeItems = _result;
      });
      this.$comm
        .getComboItems("MDM_PROCESS_TRANSPORT_TYPE_CD")
        .then((_result) => {
          this.transportTypeItems = _result;
        });
      this.$comm
        .getComboItems("MDM_PROCESS_HANDLING_TYPE_CD")
        .then((_result) => {
          this.handlingTypeItems = _result;
        });
      this.$comm
        .getComboItems("RAM_RISK_PROCESS_SPECIAL_EDU_TARGET_CD")
        .then((_result) => {
          this.specialEduItems = _result;
        });
      // list setting
      this.getDetail();
    },
    getDetail() {
      this.$http.url = this.$format(
        this.detailUrl,
        this.param.ramRiskAssessmentPlanId
      );
      this.$http.type = "GET";
      this.$http.request((_result) => {
        this.$_.extend(this.assessPlan, _result.data);
        this.$_.forEach(this.assessPlan.processes, (process) => {
          this.$set(process, "_rows", this.buildRows(process));
        });
        this.fetchAccidentNames();
      });
    },
    // 전체 공정의 accidentHistoryDesc/nearMissDesc(콤마 구분 iimAccidentId)에서
    // 중복 없이 id를 모아 이름을 한 번씩만 조회 후 캐싱
    fetchAccidentNames() {
      let ids = [];
      this.$_.forEach(this.assessPlan.processes, (process) => {
        ids = ids.concat(this.parseIds(process.accidentHistoryDesc));
        ids = ids.concat(this.parseIds(process.nearMissDesc));
      });
      ids = this.$_.uniq(ids);
      this.$_.forEach(ids, (id) => {
        if (this.accidentNameMap[id]) return;
        this.$http.url = this.$format(this.accidentDetailUrl, id);
        this.$http.type = "GET";
        this.$http.request((_result) => {
          if (_result.data) {
            this.$set(this.accidentNameMap, id, _result.data.accidentName);
          }
        });
      });
    },
    parseIds(str) {
      return str
        ? str
            .split(",")
            .map((v) => v.trim())
            .filter((v) => v)
        : [];
    },
    // id 콤마 문자열 -> 사고명 콤마 문자열 (캐시에 없으면 로딩 전이라 id 그대로 표시)
    getAccidentNames(str) {
      let ids = this.parseIds(str);
      if (ids.length === 0) return "없음";
      return ids.map((id) => this.accidentNameMap[id] || id).join(", ");
    },
    // "설비명[수량], 설비명[수량]" -> [{name, qty}]
    parseEquipItems(str) {
      let items = [];
      if (!str) return items;
      const regex = /([^,[\]]+)\[([^\]]*)\]/g;
      let match;
      while ((match = regex.exec(str)) !== null) {
        items.push({ name: match[1].trim(), qty: match[2].trim() });
      }
      return items;
    },
    // "화학물질명[일취급량, 취급시간], ..." -> [{name, qty, time}]
    parseChemItems(str) {
      let items = [];
      if (!str) return items;
      const regex = /([^,[\]]+)\[([^\]]*)\]/g;
      let match;
      while ((match = regex.exec(str)) !== null) {
        let inner = match[2].split(",").map((v) => v.trim());
        items.push({
          name: match[1].trim(),
          qty: inner[0] || "",
          time: inner[1] || "",
        });
      }
      return items;
    },
    buildRows(process) {
      let rows = [];
      if (process.fmScenarios && process.fmScenarios.length > 0) {
        // 단위작업 하나에 유해위험요인이 여러 건이면 fmScenarios에 같은 단위작업이
        // 여러 행으로 존재함 -> mdmSopId 기준으로 중복 제거 후 표시
        let uniqScenarios = this.$_.uniqBy(
          process.fmScenarios,
          (scenario) => scenario.mdmSopId
        );
        this.$_.forEach(uniqScenarios, (scenario) => {
          let equipItems = this.parseEquipItems(scenario.equipmentList);
          let chemItems = this.parseChemItems(scenario.chemicalList);
          let rowCount = Math.max(equipItems.length, chemItems.length, 1);
          for (let i = 0; i < rowCount; i++) {
            let eq = equipItems[i] || {};
            let ch = chemItems[i] || {};
            rows.push({
              unitWorkName: scenario.jobName,
              isFirstWorkRow: i === 0,
              workRowSpan: rowCount,
              equipName: eq.name || "",
              equipQty: eq.qty || "",
              chemName: ch.name || "",
              chemQty: ch.qty || "",
              chemTime: ch.time || "",
            });
          }
        });
      }
      if (rows.length === 0) {
        rows.push({
          unitWorkName: "",
          isFirstWorkRow: true,
          workRowSpan: 1,
          equipName: "",
          equipQty: "",
          chemName: "",
          chemQty: "",
          chemTime: "",
        });
      }
      return rows;
    },
    isSelected(value, code) {
      if (!value || !code) return false;
      return value
        .toString()
        .split(",")
        .map((v) => v.trim())
        .includes(code);
    },
  },
};
</script>
<style lang="scss" scoped>
.riskSheet {
  overflow-x: auto;
}
.processBigTitle {
  font-size: 20px;
  font-weight: 800;
  background: #2c3e50;
  color: #fff;
  padding: 8px 12px;
  border-radius: 4px 4px 0 0;
}
.headerTable,
.riskTable {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 12px;

  th,
  td {
    border: 1px solid #999;
    padding: 4px 6px;
    vertical-align: middle;
  }
}
.hLabel {
  background: #f2f2f2;
  text-align: center;
  font-weight: 600;
}
.hValue {
  text-align: left;
}
.mainTitle {
  text-align: center;
  font-size: 26px;
  font-weight: 700;
}
.tHead {
  background: #ffff66;
  text-align: center;
  font-weight: 700;
}
.tSubHead {
  background: #ffffcc;
  text-align: center;
  font-weight: 600;
}
.cLeft {
  text-align: left;
}
.cCenter {
  text-align: center;
}
.etcInfoCell {
  vertical-align: top;
  text-align: left;
}
.etcRow {
  margin: 4px 0 2px;
  font-weight: 600;
}
.etcSub {
  margin-left: 12px;
  font-weight: 400;
}
.chipGrid {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 4px;
}
.chip {
  display: inline-block;
  padding: 1px 6px;
  margin-right: 4px;
  border: 1px solid #bbb;
  border-radius: 3px;
  background: #f5f5f5;
  font-weight: 400;
}
.chipOn {
  background: #8fd98f;
  border-color: #4caf50;
  font-weight: 700;
}
</style>
